import React from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Button, Chip, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";

import UnauthorizedAccess from "components/RequirePermission/UnauthorizedAccess";
import { useDashboardSettings } from "pages/admin/components/CopilotDashboard/useDashboardSettings";
import RefreshButton from "components/shared/RefreshButton/RefreshButton";
import { useGetMeteredUsageTrendQuery, useGetMeteredUsageUserDetailQuery } from "state/rtkQuery/user/userQueries";
import { useAccessPlatformId } from "utils/accessManagement/useAccessPlatformId";

import { availableMetrics, deloitteLabelPercent, hasDeloitteFields, formatMoney, formatShare, metricLabel, productColorMap, rangeError, resolveRange } from "./meteredFormat";
import { useMeteredUsageRefresh } from "./useMeteredUsageRefresh";
import { PersonBudgetCard, PersonProfileCard } from "./MeteredPersonProfile";
import { ACCENT, Card, DELOITTE_GREEN, EmptyNote, HeaderDatePill, ProductsTable, TrendChart, bodyCellSx, headCellSx } from "./MeteredParts";

const pillSx = (active) => ({
  borderRadius: "16px",
  textTransform: "none",
  fontSize: "0.8rem",
  fontWeight: active ? 600 : 400,
  backgroundColor: active ? ACCENT : "transparent",
  color: active ? "#fff" : "#555",
  border: `1px solid ${active ? ACCENT : "#ccc"}`,
  px: 1.5,
  py: 0.3,
  minWidth: 0,
  "&:hover": { backgroundColor: active ? "#1f6e0a" : "#f0f0f0", border: `1px solid ${active ? "#1f6e0a" : "#bbb"}` },
});

// The orgs / cost centers one person was billed to, with the amount for each and its share of the person's total.
const BilledTable = ({ rows, nameKey, idKey, emptyName, header, metric, totals }) => {
  const whole = totals?.[metric] ?? 0;
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headCellSx}>{header}</TableCell>
            <TableCell sx={headCellSx} align="right">{metricLabel(metric)}</TableCell>
            <TableCell sx={{ ...headCellSx, width: 140 }}>Share</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row[idKey] ?? row[nameKey] ?? emptyName} data-testid="billed-row" sx={{ "&:last-child td": { border: 0 } }}>
              <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{row[nameKey] ?? emptyName}</TableCell>
              <TableCell sx={{ ...bodyCellSx, fontVariantNumeric: "tabular-nums" }} align="right">{formatMoney(row[metric])}</TableCell>
              <TableCell sx={bodyCellSx}>
                <Typography sx={{ fontSize: "0.75rem", color: "#555" }}>{formatShare(row[metric] ?? 0, whole)}</Typography>
                <Box sx={{ height: 5, borderRadius: 4, backgroundColor: "#eee", overflow: "hidden" }}>
                  <Box sx={{ height: "100%", width: `${whole > 0 ? Math.min(Math.max(((row[metric] ?? 0) / whole) * 100, 1), 100) : 0}%`, backgroundColor: DELOITTE_GREEN }} />
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// One person's billing usage for the whole chosen period: identity, trend, product / SKU breakdown with units, and
// the organizations and cost centers they were billed to. The endpoint takes no product, SKU or cost center filter, so
// this page always shows the person's whole-period usage even when it was opened from a filtered list — and says so.
// The list row they came from (router state) is only a fallback for the name while the response loads.
const PersonLoadError = ({ onRetry }) => (
  <EmptyNote>
    Couldn't load this person's usage.{" "}
    <Button size="small" onClick={onRetry} sx={{ textTransform: "none", color: ACCENT, fontStyle: "normal" }}>
      Retry
    </Button>
  </EmptyNote>
);

const MeteredUsageUserDetail = ({ scope, caps }) => {
  const { orgId: routeOrgId, userId } = useParams();
  const orgId = scope === "org" ? routeOrgId : undefined;
  const enterprise = scope === "enterprise";
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { platformId } = useAccessPlatformId();
  const range = resolveRange(searchParams);
  const person = location.state?.user ?? null;

  const { deloitteOverageRate } = useDashboardSettings(); // the AI Credits SKU row shows what Deloitte pays
  const rangeProblem = rangeError(range);
  const { data, isFetching, isError, refetch } = useGetMeteredUsageUserDetailQuery(
    { scope, platformId, orgId, userId, fromDate: range.from, toDate: range.to },
    { skip: !platformId || !userId || (!enterprise && !orgId) || !caps.canViewUserDetail || Boolean(rangeProblem) }
  );
  // The Deloitte measures are only offered when the API returns the Deloitte figures.
  const metrics = availableMetrics(hasDeloitteFields(data?.totals));
  const metric = metrics.some((m) => m.key === searchParams.get("metric")) ? searchParams.get("metric") : "net_amount";
  const discountPercent = deloitteLabelPercent(undefined, deloitteOverageRate);

  // The person endpoint's own trend has no product on it, so the stacked-by-product trend comes from the trend endpoint
  // filtered to this person. Without the trend permission the plain daily trend from the person response is shown.
  const stackedTrend = Boolean(caps.canViewTrend);
  const trend = useGetMeteredUsageTrendQuery(
    { scope, platformId, orgId, userId, fromDate: range.from, toDate: range.to, granularity: "day", groupBy: "product" },
    { skip: !platformId || !userId || (!enterprise && !orgId) || !stackedTrend || Boolean(rangeProblem) }
  );

  // Same shared refresh as the dashboard (button + timed): invalidates the MeteredUsage tag.
  const refresh = useMeteredUsageRefresh();

  // Back goes to the page they came from. From the enterprise page with an organization picked that is the enterprise
  // page with the same organization, not the organization's own page; otherwise it is this scope's dashboard.
  const returnTo = typeof location.state?.returnTo === "string" && location.state.returnTo.startsWith("/ecr-ops/metered-usage") ? location.state.returnTo : null;
  const backPath = enterprise ? "/ecr-ops/metered-usage" : `/admin/${orgId}/metered-usage`;
  const backQuery = new URLSearchParams({ preset: "custom", from: range.from, to: range.to }).toString();
  const backTo = returnTo ?? `${backPath}?${backQuery}`;

  if (!caps.canViewUserDetail) {
    return <UnauthorizedAccess message="You do not have permission to view a person's metered usage." primaryLabel="Back" primaryTo={returnTo ?? backPath} />;
  }

  const totals = data?.totals;
  const isInitialLoading = !data && isFetching;
  const displayName = data?.full_name ?? data?.user_name ?? person?.full_name ?? person?.user_name ?? "Person";
  const displayEmail = data?.email ?? person?.email ?? data?.user_name ?? userId;
  const billedOrgs = data?.organizations ?? [];
  const billedCostCenters = data?.cost_centers ?? [];

  return (
    <Box
      sx={{
        flex: "1",
        marginTop: enterprise ? 0 : { xs: "1.5rem", md: "4rem" },
        marginBottom: enterprise ? 0 : { xs: "1.5rem", md: "4rem" },
        marginLeft: enterprise ? 0 : { xs: "1rem", md: "4rem" },
        marginRight: enterprise ? 0 : { xs: "1rem", md: "4rem" },
        backgroundColor: "#F3F3F3",
        borderRadius: "12px",
        padding: { xs: "16px", md: "24px" },
        minWidth: 0,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Back to Metered Usage" arrow>
            <IconButton size="small" aria-label="Back to Metered Usage" onClick={() => navigate(backTo)} sx={{ color: "#555", ml: -0.5 }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Box>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#43B02A", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5 }}>
              Metered Usage
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#1a1a1a", lineHeight: 1.2 }}>
              {displayName}
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "#888", mt: 0.3 }}>{displayEmail}</Typography>
            <HeaderDatePill from={range.from} to={range.to} />
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: "0.06em" }}>Net this period</Typography>
            <Typography sx={{ fontSize: "1.6rem", fontWeight: 700, color: "#1a1a1a", lineHeight: 1.1 }}>{isInitialLoading ? "…" : formatMoney(totals?.net_amount)}</Typography>
            <Typography sx={{ fontSize: "0.72rem", color: "#999" }}>
              gross {isInitialLoading ? "…" : formatMoney(totals?.gross_amount)} · discount {isInitialLoading ? "…" : formatMoney(totals?.discount_amount)}
            </Typography>
          </Box>
          <RefreshButton label="Refresh this person's usage" isRefreshing={isFetching || trend.isFetching} onClick={refresh} size={30} />
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Chip
          data-testid="person-scope"
          label="Whole-person usage for this period — not narrowed by the product, SKU or cost center you filtered the dashboard by"
          sx={{ alignSelf: "flex-start", height: "auto", py: 0.6, backgroundColor: "#eef5fb", color: "#12475f", "& .MuiChip-label": { whiteSpace: "normal", fontSize: "0.78rem" } }}
        />
        {rangeProblem && (
          <Typography role="alert" sx={{ color: "#E53935", fontSize: "0.85rem" }}>
            {rangeProblem} Nothing is loaded until the dates are fixed.
          </Typography>
        )}
        {/* Who they are and their Copilot budget, as on the Copilot dashboard. The budget is per organization, so enterprise gets the profile only. */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: enterprise ? "1fr" : "1fr 1fr" }, gap: 3, alignItems: "stretch" }}>
          <PersonProfileCard email={data?.email ?? person?.email} userId={userId} platformId={platformId} range={range} />
          {!enterprise && <PersonBudgetCard email={data?.email ?? person?.email} orgId={orgId} platformId={platformId} range={range} />}
        </Box>
        <Card
          title="Cost Trend"
          subtitle={`Days with no usage have no bar${stackedTrend ? " · stacked by product" : ""}`}
          from={range.from}
          to={range.to}
          loading={stackedTrend ? trend.isFetching : isFetching}
          isInitialLoading={stackedTrend ? !trend.data && trend.isFetching : isInitialLoading}
          skeleton="chart"
          actions={
            <Box sx={{ display: "flex", gap: 0.5 }} role="group" aria-label="Measure">
              {metrics.map((m) => (
                <Button
                  key={m.key}
                  size="small"
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    if (m.key === "net_amount") next.delete("metric");
                    else next.set("metric", m.key);
                    setSearchParams(next, { replace: true });
                  }}
                  sx={pillSx(metric === m.key)}
                >
                  {m.label}
                </Button>
              ))}
            </Box>
          }
        >
          {stackedTrend ? (
            trend.isError ? (
              <PersonLoadError onRetry={trend.refetch} />
            ) : (
              <TrendChart points={trend.data?.data} granularity="day" metric={metric} groupBy={trend.data?.group_by ?? undefined} colors={productColorMap(data?.products)} />
            )
          ) : isError ? (
            <PersonLoadError onRetry={refetch} />
          ) : (
            <TrendChart points={data?.trend} granularity="day" metric={metric} />
          )}
        </Card>
        <Card
          title="Products & SKUs"
          subtitle="Quantities are per SKU and never added across units"
          from={range.from}
          to={range.to}
          loading={isFetching}
          isInitialLoading={isInitialLoading}
          skeleton="table"
        >
          {isError ? <PersonLoadError onRetry={refetch} /> : <ProductsTable products={data?.products} deloitteRate={deloitteOverageRate} discountPercent={discountPercent} range={range} />}
        </Card>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3 }}>
          <Card title="Billed to Organizations" subtitle={`${metricLabel(metric)} by organization`} from={range.from} to={range.to} loading={isFetching} isInitialLoading={isInitialLoading} skeleton="table" skeletonRows={3}>
            {isError ? <PersonLoadError onRetry={refetch} /> : billedOrgs.length === 0 ? <EmptyNote>No organization amounts in this period.</EmptyNote> : <BilledTable rows={billedOrgs} nameKey="org_name" idKey="org_id" emptyName="Unmapped" header="Organization" metric={metric} totals={totals} />}
          </Card>
          <Card title="Billed to Cost Centers" subtitle={`${metricLabel(metric)} by cost center`} from={range.from} to={range.to} loading={isFetching} isInitialLoading={isInitialLoading} skeleton="table" skeletonRows={3}>
            {isError ? <PersonLoadError onRetry={refetch} /> : billedCostCenters.length === 0 ? <EmptyNote>No cost center amounts in this period.</EmptyNote> : <BilledTable rows={billedCostCenters} nameKey="cost_center_name" idKey="cost_center_id" emptyName="Unassigned" header="Cost center" metric={metric} totals={totals} />}
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default MeteredUsageUserDetail;
